import * as React from "react";
import { CSSTransition } from "react-transition-group";
import { FocusScope } from "@react-aria/focus";

import {
  CSSRulesFunction,
  useTheme,
  ResponsiveValue,
  Color,
  ZIndex,
  SpaceScale,
  Interpolation,
  ColorPreset,
} from "../theme";
import { Glyph } from "../icons";
import BodyPortal from "../BodyPortal";
import { ButtonVariant, IconButton } from "../buttons";
import { Space } from "../layout";

import Overlay from "./Overlay";

interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  /** Sets the root element of the dialog to a form or plain div */
  as: "form";
}

interface DivProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Sets the root element of the dialog to a form or plain div */
  as?: "div";
}

export type DialogProps = (DivProps | FormProps) & {
  /**
   * Toggles the dialog's visibility
   */
  open?: boolean;

  /**
   * Sets the content in the header of the dialog
   */
  header?: React.ReactNode;

  /**
   * Sets the content in the footer of the dialog
   */
  footer?: React.ReactNode;

  /**
   * Configures the close button on the dialog. If this is not set, the close
   * button will not be displayed.
   */
  closeAction?: {
    /**
     * The action to perform when the close button is clicked
     */
    onClose(): void;
    /**
     * The label to use on the close button for assistive technology
     */
    label: React.ReactNode;
  };

  children: React.ReactNode;
};

const vars = {
  // the top and right absolute position of the close button
  closePull: "--dialog-close-pull",
  // the amount of right padding to add to the header so it clears the close
  // button
  closeClearing: "--dialog-close-clearing",

  gutterTop: "--dialog-gutter-top",
  gutterBottom: "--dialog-gutter-bottom",
  headerGutterV: "--dialog-header-gutterv",
  headerGutterH: "--dialog-header-gutterh",
  headerGridGap: "--dialog-header-grid-gap",
  footerGutterV: "--dialog-footer-gutterv",
  footerGutterH: "--dialog-footer-gutterh",
  bodyGutterV: "--dialog-body-gutterv",
  bodyGutterH: "--dialog-body-gutterh",

  width: "--dialog-width",
};

const dialogSizeTokens: CSSRulesFunction = (theme) => {
  const gutterH: ResponsiveValue<SpaceScale> = [2, null, 3];
  const bodyGutterV: ResponsiveValue<SpaceScale> = [1.5, null, 2];
  const headerGutterV: ResponsiveValue<SpaceScale> = [1, null, 1.5];
  const footerGutterV: ResponsiveValue<SpaceScale> = [1, null, 2];
  const width: ResponsiveValue<string | number> = ["100%", null, "600px"];

  return [
    {
      [vars.gutterBottom]: theme.spacing(1),
      [vars.gutterTop]: theme.spacing(1),
      [vars.headerGridGap]: theme.spacing(1),
      [vars.closePull]: theme.spacing(0.5),
      [vars.closeClearing]: theme.spacing(1),
    },
    theme.responsive(width, (w) => {
      return { [vars.width]: w };
    }),
    theme.responsive(headerGutterV, (v) => {
      return { [vars.headerGutterV]: theme.spacing(v) };
    }),
    theme.responsive(footerGutterV, (v) => {
      return { [vars.footerGutterV]: theme.spacing(v) };
    }),
    theme.responsive(bodyGutterV, (v) => {
      return { [vars.bodyGutterV]: theme.spacing(v) };
    }),
    theme.responsive(gutterH, (v) => {
      return {
        [vars.headerGutterH]: theme.spacing(v),
        [vars.footerGutterH]: theme.spacing(v),
        [vars.bodyGutterH]: theme.spacing(v),
      };
    }),
  ];
};

const dialogStyle: CSSRulesFunction = (theme) => {
  const tv = "--dialog-transform";
  const tvar = `var(${tv})`;
  const ov = "--dialog-opacity";
  const ovar = `var(${ov})`;

  return [
    dialogSizeTokens(theme),
    {
      margin: "auto",
      position: "relative",
      backgroundColor: theme.color(Color.Brownstone_100),
      borderRadius: theme.radius(1.5),
      webkitOverflowScrolling: "touch",
      paddingTop: `var(${vars.gutterTop})`,
      paddingBottom: `var(${vars.gutterBottom})`,
      width: `var(${vars.width})`,
      maxHeight: "100%",
      outline: "none",
    },

    {
      [tv]: "none",
      [ov]: 1,

      transition: "transform 250ms, opacity 250ms",
      transform: tvar,
      opacity: ovar,

      ".slide-enter &": {
        [tv]: "translate3d(0, 48px, 0)",
        [ov]: 0,
      },
      ".slide-enter-active &": {
        [tv]: "translate3d(0, 0, 0)",
        [ov]: 1,
      },

      ".slide-exit &": {
        [tv]: "translate3d(0, 0, 0)",
        [ov]: 1,
      },
      ".slide-exit-active &": {
        [tv]: "translate3d(0, -48px, 0)",
        [ov]: 0,
      },
      ".slide-exit-done &": {
        [tv]: "translate3d(0, -48px, 0)",
        [ov]: 0,
      },

      "@media (prefers-reduced-motion: reduce)": {
        ".slide-enter &": {
          [tv]: "translate3d(0, 8px, 0)",
          [ov]: 0,
        },
        ".slide-enter-active &": {
          [tv]: "translate3d(0, 0, 0)",
          [ov]: 1,
        },

        ".slide-exit &": {
          [tv]: "translate3d(0, 0, 0)",
          [ov]: 1,
        },
        ".slide-exit-active &": {
          [tv]: "translate3d(0, -8px, 0)",
          [ov]: 0,
        },
        ".slide-exit-done &": {
          [tv]: "translate3d(0, -8px, 0)",
          [ov]: 0,
        },
      },
    },
  ];
};

const dialogHeaderStyle: CSSRulesFunction = (theme) => ({
  paddingTop: `var(${vars.headerGutterV})`,
  paddingBottom: `var(${vars.headerGutterV})`,
  paddingRight: `calc(var(${vars.headerGutterH}) + var(${vars.closeClearing}))`,
  paddingLeft: `var(${vars.headerGutterH})`,
  borderBottom: `1px solid ${theme.color(ColorPreset.BorderOnLight_04)}`,
  display: "grid",
  gap: `var(${vars.headerGridGap})`,
});

const dialogFooterStyle: CSSRulesFunction = (theme) => ({
  borderTop: `1px solid ${theme.color(ColorPreset.BorderOnLight_04)}`,
  padding: `var(${vars.footerGutterV}) var(${vars.footerGutterH})`,
});

const dialogBodyStyle: Interpolation = {
  padding: `var(${vars.bodyGutterV}) var(${vars.bodyGutterH})`,
  overflowY: "auto",
};

const dialogMainWrapperStyle: Interpolation = {
  height: "100%",
  maxHeight: "100%",
  display: "grid",
};

const dialogHostStyle: CSSRulesFunction = (theme) => {
  return [
    theme.responsive<SpaceScale>([3, null, 4], (val) => ({
      paddingTop: theme.spacing(val),
      paddingBottom: theme.spacing(val),
    })),
    {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: ZIndex.Dialog,
      display: "flex",
      flexDirection: "column",
    },
  ];
};

const closeStyle: Interpolation = {
  position: "absolute",
  top: `var(${vars.closePull})`,
  right: `var(${vars.closePull})`,
};

const CloseTrigger: React.FC<{
  onClose: () => void;
  label: React.ReactNode;
}> = (props) => {
  return (
    <div css={closeStyle}>
      <IconButton
        icon={Glyph.Close}
        label={props.label}
        variant={ButtonVariant.TextOnLight}
        onClick={props.onClose}
      />
    </div>
  );
};

const Dialog: React.FC<DialogProps> = (props) => {
  const { theme } = useTheme();

  const { open, closeAction, header, footer, children, ...rest } = props;

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key !== "Escape" || !closeAction?.onClose) return;

    // We don't want the event to propagate up in case anyone is listening to
    // the Escape key on the body.
    event.stopPropagation();

    closeAction.onClose();
  };

  const enableHeader = header && React.Children.count(header) > 0;
  const enableFooter = footer && React.Children.count(footer) > 0;

  const sharedProps = {
    as: undefined,
  } as const;

  const content = (
    <>
      {" "}
      {closeAction ? <CloseTrigger {...closeAction} /> : null}
      {enableHeader ? (
        <div css={dialogHeaderStyle(theme)}>{header}</div>
      ) : closeAction ? (
        <Space v={[1, null, 2]} />
      ) : null}
      <div css={dialogBodyStyle}>{props.children}</div>
      {enableFooter ? <div css={dialogFooterStyle(theme)}>{footer}</div> : null}
    </>
  );

  return (
    <BodyPortal>
      <CSSTransition
        in={open}
        timeout={250}
        classNames="slide"
        mountOnEnter
        unmountOnExit
        appear
      >
        <div onKeyDown={handleKeyDown} role="presentation" tabIndex={-1}>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <FocusScope contain autoFocus restoreFocus>
            <Overlay />
            <div css={dialogHostStyle(theme)}>
              <div
                css={dialogStyle(theme)}
                role="dialog"
                aria-modal
                tabIndex={-1}
              >
                {rest.as === "form" ? (
                  <form css={dialogMainWrapperStyle} {...rest} {...sharedProps}>
                    {content}
                  </form>
                ) : (
                  <div css={dialogMainWrapperStyle} {...rest} {...sharedProps}>
                    {content}
                  </div>
                )}
              </div>
            </div>
          </FocusScope>
        </div>
      </CSSTransition>
    </BodyPortal>
  );
};

export default Dialog;
